// Threads chat API client — the /threads page (server-persisted chat threads,
// routes/v2/chat/threads.py). Deliberately SEPARATE from `gsGraph.js`: that one
// belongs to the graph lane and this page may not import from or edit it.
//
// Two transports, per the house split: JSON round trips ride `AxiosClient`
// (which injects `x-access-token` from localStorage["user"]), and the ask
// stream bypasses axios entirely — axios cannot stream a response body, so the
// NDJSON reader is raw `fetch` (same reasoning as `gsGraph.js`).
//
// Every read helper resolves the PAYLOAD (not the axios envelope) so callers
// never unwrap `.data` themselves; failures reject and are the caller's to
// catch — this module never swallows an error into a silent null.
import { AxiosClient } from "./AxiosClient";
import { BASE_URL } from "./config";

// Server caps and paginates; these are the page's requested window sizes.
const THREAD_PAGE_SIZE = 30;
const MESSAGE_PAGE_SIZE = 50;

// ── thread CRUD ──────────────────────────────────────────────────────────────

// → { threads: [{thread_id, title, created_at, last_msg_at, turn_count,
//                last_scope}], total, next_offset }
// `last_scope` is "global" or a lens ref {kind, id, name}. `next_offset` is
// null/absent on the last page.
export const listThreads = (offset = 0) =>
  AxiosClient.get(
    `v2/chat/threads?offset=${offset}&limit=${THREAD_PAGE_SIZE}`,
  ).then((res) => ({
    threads: res?.data?.threads || [],
    total: res?.data?.total ?? 0,
    next_offset: res?.data?.next_offset ?? null,
  }));

// → { thread } — a fresh empty thread ("New thread"); the page creates one
// lazily on the first send, so a draft that is never sent costs nothing.
export const createThread = () =>
  AxiosClient.post("v2/chat/threads", {}).then((res) => ({
    thread: res?.data?.thread || null,
  }));

export const renameThread = (threadId, title) =>
  AxiosClient.patch(`v2/chat/threads/${threadId}`, { title }).then(
    (res) => res?.data,
  );

export const deleteThread = (threadId) =>
  AxiosClient.delete(`v2/chat/threads/${threadId}`).then((res) => res?.data);

// → { thread, messages, scope_missing, total, next_offset }
// `scope_missing` is true when the thread's stored lens ref dangles (renamed or
// deleted preset): the server already fell back to global and the picker says
// so ("Global (lens deleted)") instead of pretending the lens still applies.
//
// MESSAGES ARE PASSED THROUGH WHOLE — no field list, no reshaping — which is
// what lets the server add per-turn fields and have them arrive: `suggestions`
// did, and `tool_use` (the tool calls a turn ran — any of the five tools,
// plan Part 6/7) does now.
// MainThreads picks the ones it renders.
export const getThreadMessages = (threadId, offset = 0) =>
  AxiosClient.get(
    `v2/chat/threads/${threadId}/messages?offset=${offset}&limit=${MESSAGE_PAGE_SIZE}`,
  ).then((res) => ({
    thread: res?.data?.thread || null,
    messages: res?.data?.messages || [],
    scope_missing: res?.data?.scope_missing === true,
    total: res?.data?.total ?? 0,
    next_offset: res?.data?.next_offset ?? null,
  }));

// ── lens pool for the composer's scope picker ────────────────────────────────
// The pickable list is the saved-preset pool, which lives on the gs-graph
// blueprint (`v2/lens` has no list endpoint — it holds ONE per-user state doc).
// Reading it from here is a deliberate cross-feature read: LensProvider is
// mounted only on lens routes, and /threads is outside it, so the picker owns
// its own fetch and its own selection state.
//
// → [{id, name, icon, color}] — tolerating BOTH the unified `{presets:[…]}`
// shape and the older `{views, map_presets}` one, so a UI/API deploy-order
// mismatch never blanks the picker (same defence as LensProvider).
export const listLenses = () =>
  AxiosClient.get("v2/gs-graph/views").then((res) => {
    const d = res?.data;
    const pool = Array.isArray(d?.presets)
      ? d.presets
      : [...(d?.views || []), ...(d?.map_presets || [])];
    return pool
      .map((p) => ({
        id: p?.id || p?.presetId || p?.document_id || "",
        name: p?.name || "Untitled lens",
        icon: p?.icon || null,
        color: p?.color || null,
      }))
      .filter((p) => p.id);
  });

// ── the thread ask stream ────────────────────────────────────────────────────
// POST /api/v2/chat/threads/<id>/ask as NDJSON over fetch. Adapted from
// `gsGraph.js:askGraphChatStream` — carry-buffer line split, malformed lines
// skipped, unknown event types dropped (which is what lets the server ship new
// events first) — with two deliberate differences:
//   · `brief` is GONE. It is a graph long-press gesture; the server never emits
//     it on this route, so neither the body nor the dispatch carries it. Same
//     for `action` events: conclusion/pills stay graph-only in v1.
//   · The INVALID-TOKEN GUARD below. `token_required` answers a bad token with
//     HTTP **200** and a text/html `{"message": "token is invalid"}` body, so
//     `res.ok` is true and the plain reader would just see a stream that ends
//     with zero events — a silent dead chat. We sniff the content type (and
//     that body) BEFORE the read loop and say "session expired" out loud.
//
// The MIMETYPE IS LOAD-BEARING on the server side (application/x-ndjson is why
// gzip skips the route and status events flush at all); nothing here may ask
// for another one.
//
// Returns an `abort()` closure.
//
// `web` (additive, optional): true = the composer's web toggle — the server
// runs the web search itself before answering (guaranteed, not a model
// choice) and answers with corpus + web sources in one citation space. Sent
// ONLY when true so an unforced ask's body is byte-identical to before the
// flag existed; a server without ZYTE_KEY answers the forced ask with a 400
// whose message flows through `onError` like any other request failure.
export const askThreadStream = (
  { threadId, question, scope, web },
  { onStatus, onCitations, onToken, onTitle, onSuggestions, onDone, onError },
) => {
  const controller = new AbortController();
  const user = localStorage.getItem("user");

  (async () => {
    try {
      const res = await fetch(BASE_URL + `v2/chat/threads/${threadId}/ask`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-access-token": user,
        },
        body: JSON.stringify(
          web === true ? { question, scope, web: true } : { question, scope },
        ),
        signal: controller.signal,
      });
      if (!res.ok) {
        let message = `request failed (${res.status})`;
        try {
          message = (await res.json()).error || message;
        } catch {
          /* non-JSON error body */
        }
        onError?.(message);
        return;
      }

      // 200-but-not-a-stream: the auth wart above, or any proxy/error page that
      // sneaks a 200 through. Never enter the read loop for one of these.
      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("ndjson")) {
        let body = "";
        try {
          body = await res.text();
        } catch {
          /* unreadable body — fall through to the generic message */
        }
        if (body.includes("token is invalid")) {
          onError?.("session expired");
          return;
        }
        let message = "unexpected response from server";
        try {
          const parsed = JSON.parse(body);
          message = parsed.error || parsed.message || message;
        } catch {
          /* not JSON either — keep the generic message */
        }
        onError?.(message);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let carry = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        carry += decoder.decode(value, { stream: true });
        const lines = carry.split("\n");
        carry = lines.pop(); // last element is a partial line (or "")
        for (const line of lines) {
          if (!line.trim()) continue;
          let event;
          try {
            event = JSON.parse(line);
          } catch {
            continue; // malformed line: skip, keep the stream
          }
          if (event.type === "status") onStatus?.(event);
          else if (event.type === "citations") onCitations?.(event);
          else if (event.type === "token") onToken?.(event.text);
          // The server owns the thread title: `provisional` early in a new
          // thread's first ask, then the LLM's in the stream tail.
          else if (event.type === "title")
            onTitle?.({
              title: String(event.title || "").trim(),
              provisional: event.provisional === true,
            });
          // Follow-up questions, coerced to strings here so no consumer has to
          // wonder what a malformed element would render as.
          else if (event.type === "suggestions")
            onSuggestions?.(
              (Array.isArray(event.questions) ? event.questions : [])
                .map((q) => String(q || "").trim())
                .filter(Boolean),
            );
          else if (event.type === "done") onDone?.(event);
          else if (event.type === "error") onError?.(event.message);
          // Unknown types are dropped, deliberately: that is what lets the
          // server ship new events first.
        }
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        onError?.(err?.message || "stream failed");
      }
    }
  })();

  return () => controller.abort();
};
